import { useEffect, useRef } from 'react';
import { useApiGateway } from 'hooks/useApi';
import { isMarketHours } from 'utils/shared/time/marketHours';

const DAILY_VOLUME_ENDPOINT = '/v1/products/option-flow/daily-volume';
const DEFAULT_POLL_INTERVAL = 5 * 60 * 1000; // 5 min, matches the server cache TTL

interface TickerDailyVolumeResponse {
  volume: number;
}

export interface UseTickerDailyVolumeOptions {
  enabled?: boolean;
  pollInterval?: number;
}

export interface UseTickerDailyVolumeReturn {
  dailyVolume: number | null;
  loading: boolean;
}

/**
 * Fetches a ticker's daily option volume from the cached, single-flight
 * `/daily-volume` endpoint instead of bundling `daily_volume(where ticker)` into every
 * polled ticker feed query. Shared across all ticker views, so a hot ticker triggers
 * at most one daily_volume DB query per cache-TTL window no matter how many viewers it
 * has. Polls only during market hours, matching the feed hooks.
 */
export function useTickerDailyVolume(
  ticker: string,
  { enabled = true, pollInterval = DEFAULT_POLL_INTERVAL }: UseTickerDailyVolumeOptions = {},
): UseTickerDailyVolumeReturn {
  const { get, data, isInitialLoading } = useApiGateway<unknown, TickerDailyVolumeResponse>(
    DAILY_VOLUME_ENDPOINT,
    { keepDataOnRefetch: true },
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled || !ticker) return undefined;

    const fetchVolume = () => {
      get({ endpoint: `?ticker=${encodeURIComponent(ticker.toUpperCase())}` });
    };

    fetchVolume();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (pollInterval > 0) {
      intervalRef.current = setInterval(() => {
        if (isMarketHours()) {
          fetchVolume();
        }
      }, pollInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // `get` is recreated each render by useApi; including it would re-run the poll
    // setup on every render. Same pattern as useTickerFlowData.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, enabled, pollInterval]);

  return {
    dailyVolume: data?.volume ?? null,
    loading: isInitialLoading,
  };
}
